import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import TopBar from '../home/components/TopBar';
import MainNav from '../home/components/MainNav';
import Footer from '../home/components/Footer';
import QuickMenu from '../../components/feature/QuickMenu';
import { useSEO, createServiceSchema } from '../../utils/seo';

export default function LiveStreamingPage() {
  useSEO({
    title: '라이브 스트리밍 - KR STUDIO | 실시간 방송 솔루션',
    description: 'KR스튜디오의 전문 라이브 스트리밍 서비스를 경험하세요. 안정적인 실시간 방송, 다채널 송출, 인터랙티브 기능을 제공합니다.',
    keywords: '라이브 스트리밍, 실시간 방송, 온라인 강의, 웨비나, 라이브 교육',
    canonical: '/live-streaming',
    schema: createServiceSchema(
      '라이브 스트리밍 서비스',
      '안정적인 실시간 방송과 다채널 송출, 인터랙티브 기능 제공'
    ),
  });

  const { t } = useTranslation();
  const [activeScenario, setActiveScenario] = useState<number | null>(null);
  const [activePlatform, setActivePlatform] = useState('zoom');

  const scenarios = [
    {
      icon: 'ri-mic-off-line',
      title: '음성이 끊겼을 때',
      steps: [
        '3초 내 백업 마이크 전환',
        '자막으로 상황 안내',
        '30초 내 완전 복구'
      ]
    },
    {
      icon: 'ri-tv-line',
      title: '화면이 멈췄을 때',
      steps: [
        '대기 화면 자동 전환',
        '백업 카메라 즉시 가동',
        '1분 내 정상 송출'
      ]
    },
    {
      icon: 'ri-signal-wifi-error-line',
      title: '발표자 접속 불량',
      steps: [
        '사전 녹화본 대체 송출',
        '전화 음성으로 진행',
        '일정 자동 조정'
      ]
    }
  ];

  const serviceSteps = [
    {
      phase: '사전 준비 (D-7)',
      icon: 'ri-calendar-check-line',
      title: '전략 수립',
      items: [
        '플랫폼 선정 (Zoom/YouTube/Vimeo)',
        '시나리오 및 큐시트 작성',
        '발표자 사전 교육',
        '접속 테스트'
      ],
      color: 'red'
    },
    {
      phase: '리허설 (D-1)',
      icon: 'ri-clapperboard-line',
      title: '완벽한 시뮬레이션',
      items: [
        '전체 진행 리허설',
        '음향/영상 품질 테스트',
        '비상 시나리오 훈련',
        '최종 체크리스트 확인'
      ],
      color: 'orange'
    },
    {
      phase: '본 송출 (D-Day)',
      icon: 'ri-broadcast-line',
      title: '실시간 운영',
      items: [
        '2시간 전 현장 도착',
        '이중 백업 시스템 구축',
        '전담 엔지니어 2인 대기',
        '실시간 모니터링'
      ],
      color: 'red'
    },
    {
      phase: '사후 관리',
      icon: 'ri-save-line',
      title: '콘텐츠 활용',
      items: [
        '고화질 녹화본 제공',
        '하이라이트 편집',
        '다시보기 업로드',
        '시청 데이터 분석'
      ],
      color: 'blue'
    }
  ];

  const platforms = {
    zoom: {
      name: 'Zoom 웨비나',
      features: [
        '최대 10,000명 동시 접속',
        '실시간 Q&A 관리',
        '소회의실 운영',
        '참석 인증서 발급'
      ]
    },
    youtube: {
      name: 'YouTube Live',
      features: [
        '무제한 시청자',
        '실시간 채팅 관리',
        '자동 아카이빙',
        '수익 창출 가능'
      ]
    },
    custom: {
      name: '자체 플랫폼',
      features: [
        '브랜딩 커스터마이징',
        '접속 인증 시스템',
        '유료 결제 연동',
        '커스텀 기능 개발'
      ]
    }
  };

  const useCases = [
    {
      category: '교육 분야',
      icon: 'ri-graduation-cap-line',
      items: ['대학 온라인 강의', '자격증 교육', '기업 연수', '국제 세미나'],
      color: 'blue'
    },
    {
      category: '기업 행사',
      icon: 'ri-building-line',
      items: ['제품 런칭쇼', '주주총회', '전사 교육', '글로벌 컨퍼런스'],
      color: 'red'
    },
    {
      category: '공공 행사',
      icon: 'ri-government-line',
      items: ['정책 설명회', '공청회', '학술대회', '시상식'],
      color: 'green'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <TopBar />
      <MainNav />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-900 via-red-800 to-gray-900 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://public.readdy.ai/ai/img_res/47c81dcfb9fa0eff09e7fbc0272fecf6.jpg"
            alt="Live Streaming Control Room"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center mb-6">
              <i className="ri-live-line text-5xl text-red-400 mr-4"></i>
              <span className="text-red-400 font-bold tracking-widest text-sm">LIVE STREAMING</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black mb-6 leading-tight">
              실시간의 긴장감,<br />
              완벽한 송출의 안정감
            </h1>
            <p className="text-2xl mb-8 text-gray-300 font-light">
              300회 무사고 라이브 스트리밍의 노하우
            </p>
            <div className="hidden bg-red-600 border-2 border-red-400 px-8 py-4 inline-flex items-center gap-3 animate-pulse">
              <i className="ri-phone-line text-3xl"></i>
              <div>
                <div className="text-sm font-bold">🔴 LIVE 긴급 지원</div>
                <a href="tel:070-8656-8134" className="text-2xl font-black hover:underline cursor-pointer">070-8656-8134</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Stats Section */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">라이브 역량</h2>
            <p className="text-xl text-gray-400">실시간 송출의 모든 것</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center p-8 bg-gray-800 hover:bg-gray-700 transition cursor-pointer">
              <div className="text-6xl font-black text-red-500 mb-3">300+</div>
              <div className="text-xl text-gray-300">누적 송출 횟수</div>
            </div>
            <div className="text-center p-8 bg-gray-800 hover:bg-gray-700 transition cursor-pointer">
              <div className="text-6xl font-black text-red-500 mb-3">99.9%</div>
              <div className="text-xl text-gray-300">송출 성공률</div>
            </div>
            <div className="text-center p-8 bg-gray-800 hover:bg-gray-700 transition cursor-pointer">
              <div className="text-6xl font-black text-red-500 mb-3">3초</div>
              <div className="text-xl text-gray-300">평균 복구 시간</div>
            </div>
            <div className="text-center p-8 bg-gray-800 hover:bg-gray-700 transition cursor-pointer">
              <div className="text-6xl font-black text-red-500 mb-3">50K+</div>
              <div className="text-xl text-gray-300">최대 동시 접속</div>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Response Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-4">
              <i className="ri-alarm-warning-line text-4xl text-red-500"></i>
              <h2 className="text-4xl font-black">위기 대응 시나리오</h2>
            </div>
            <p className="text-xl text-gray-600">🚨 만약의 상황, 확실한 대응</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {scenarios.map((scenario, index) => (
              <div 
                key={index}
                className="bg-gray-50 p-8 hover:shadow-xl transition-all cursor-pointer border-2 border-transparent hover:border-red-500"
                onClick={() => setActiveScenario(activeScenario === index ? null : index)}
              >
                <div className="w-16 h-16 bg-red-500/10 flex items-center justify-center mb-6">
                  <i className={`${scenario.icon} text-3xl text-red-500`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-6">{scenario.title}</h3>
                <div className={`space-y-3 transition-all ${activeScenario === index ? 'opacity-100' : 'opacity-60'}`}>
                  {scenario.steps.map((step, idx) => (
                    <div key={idx} className="flex items-start">
                      <i className="ri-arrow-right-line text-red-500 mr-3 mt-1"></i>
                      <span className="text-gray-700">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Workflow Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">서비스 구성</h2>
            <p className="text-xl text-gray-600">체계적인 4단계 프로세스</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {serviceSteps.map((step, index) => (
              <div key={index} className="bg-white p-8 hover:shadow-xl transition-all cursor-pointer">
                <div className={`w-16 h-16 bg-${step.color}-500/10 flex items-center justify-center mb-6`}>
                  <i className={`${step.icon} text-3xl text-${step.color}-500`}></i>
                </div>
                <div className={`text-sm font-bold text-${step.color}-500 mb-2`}>{step.phase}</div>
                <h3 className="text-2xl font-bold mb-6">{step.title}</h3>
                <ul className="space-y-3">
                  {step.items.map((item, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-700">
                      <i className={`ri-checkbox-circle-fill text-${step.color}-500 mr-2 mt-0.5`}></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Features Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">플랫폼별 특화 기능</h2>
            <p className="text-xl text-gray-600">최적의 플랫폼 선택</p>
          </div>
          
          {/* Platform Tabs */}
          <div className="flex justify-center gap-4 mb-12">
            <button
              onClick={() => setActivePlatform('zoom')}
              className={`px-8 py-4 font-bold transition whitespace-nowrap cursor-pointer ${
                activePlatform === 'zoom'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Zoom 웨비나
            </button>
            <button
              onClick={() => setActivePlatform('youtube')}
              className={`px-8 py-4 font-bold transition whitespace-nowrap cursor-pointer ${
                activePlatform === 'youtube'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              YouTube Live
            </button>
            <button
              onClick={() => setActivePlatform('custom')}
              className={`px-8 py-4 font-bold transition whitespace-nowrap cursor-pointer ${
                activePlatform === 'custom'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              자체 플랫폼
            </button>
          </div>

          {/* Platform Content */}
          <div className="max-w-3xl mx-auto bg-gray-50 p-12">
            <h3 className="text-3xl font-bold mb-8 text-center">
              {platforms[activePlatform as keyof typeof platforms].name}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {platforms[activePlatform as keyof typeof platforms].features.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <i className="ri-check-double-line text-2xl text-red-500 mr-3"></i>
                  <span className="text-lg text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4">적용 사례</h2>
            <p className="text-xl text-gray-600">다양한 분야의 라이브 스트리밍</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white p-8 hover:shadow-xl transition-all cursor-pointer">
                <div className={`w-16 h-16 bg-${useCase.color}-500/10 flex items-center justify-center mb-6`}>
                  <i className={`${useCase.icon} text-3xl text-${useCase.color}-500`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-6">{useCase.category}</h3>
                <ul className="space-y-3">
                  {useCase.items.map((item, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className={`ri-arrow-right-s-line text-${useCase.color}-500 mr-2`}></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-red-900 to-gray-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <i className="ri-live-line text-6xl text-red-400 mb-6 inline-block"></i>
          <h2 className="text-4xl font-black mb-6">완벽한 라이브 송출을 경험하세요</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            300회 무사고 송출 경험을 바탕으로<br />
            귀사의 중요한 순간을 안전하게 전달합니다
          </p>
          <div className="hidden flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:070-8656-8134" className="inline-block bg-red-500 text-white px-12 py-4 text-lg font-bold hover:bg-red-600 transition cursor-pointer whitespace-nowrap">
              견적 문의
            </a>
            <a href="tel:070-8656-8134" className="inline-block border-2 border-white text-white px-12 py-4 text-lg font-bold hover:bg-white hover:text-gray-900 transition cursor-pointer whitespace-nowrap">
              긴급 지원 전화
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <QuickMenu />
    </div>
  );
}